const BASE = process.env.REACT_APP_API_URL || '';

async function request(path, options = {}) {
  const res = await fetch(BASE + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  // Auth
  login:       (u, p, r) => request('/api/auth/login',  { method: 'POST', body: { username: u, password: p, rememberMe: !!r } }),
  adminLogin:  (u, p, r) => request('/api/admin/login', { method: 'POST', body: { username: u, password: p, rememberMe: !!r } }),
  logout:      ()     => request('/api/auth/logout',  { method: 'POST' }),
  me:          ()     => request('/api/auth/me'),

  // Account
  getAccount:        ()      => request('/api/auth/account'),
  updateAccountEmail:(email) => request('/api/auth/account/email', { method: 'PUT', body: { email } }),

  // Forgot / reset
  forgotDetails:    (mode, identifier) => request('/api/auth/forgot', { method: 'POST', body: { mode, identifier } }),
  validateToken:    (token)            => request(`/api/auth/validate-token/${token}`),
  resetPassword:    (token, password)  => request(`/api/auth/reset-password/${token}`, { method: 'POST', body: { password } }),
  changeUsername:   (token, body)      => request(`/api/auth/change-username/${token}`, { method: 'POST', body }),

  // Admin
  adminGetRestaurants:      ()           => request('/api/admin/restaurants'),
  adminCreateRestaurant:    (data)       => request('/api/admin/restaurants', { method: 'POST', body: data }),
  adminDeleteRestaurant:    (id)         => request(`/api/admin/restaurants/${id}`, { method: 'DELETE' }),
  adminSetPassword:         (id, pwd)    => request(`/api/admin/restaurants/${id}/password`, { method: 'PUT', body: { password: pwd } }),
  adminSetStatus:           (id, active) => request(`/api/admin/restaurants/${id}/status`, { method: 'PUT', body: { active } }),
  adminSetEmail:            (id, email)  => request(`/api/admin/restaurants/${id}/email`, { method: 'PUT', body: { email } }),
  adminGetApplications:     ()           => request('/api/admin/applications'),
  adminApproveApplication:  (id, data)   => request(`/api/admin/applications/${id}/approve`, { method: 'POST', body: data }),
  adminRejectApplication:   (id)         => request(`/api/admin/applications/${id}`, { method: 'DELETE' }),
  adminGetPlatformAnalytics:()           => request('/api/admin/platform-analytics'),
  adminGetActivity:         ()           => request('/api/admin/activity'),
  adminGetRestaurantActivity: (id)       => request(`/api/admin/restaurants/${id}/activity`),
  adminDownloadTaxCSV: async () => {
    const BASE = process.env.REACT_APP_API_URL || '';
    const res = await fetch(BASE + '/api/admin/export/tax-csv', { credentials: 'include' });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `seva-btw-export-${new Date().getFullYear()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  // Owner password / tab lock
  setOwnerPassword:      (currentPassword, newPassword) => request('/api/settings/owner-password', { method: 'POST', body: { currentPassword, newPassword } }),
  verifyOwnerPassword:   (password) => request('/api/settings/verify-owner-password', { method: 'POST', body: { password } }),

  // Reservations
  getReservations: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request(`/api/reservations${qs ? '?' + qs : ''}`);
  },
  createReservation: (data)     => request('/api/reservations',      { method: 'POST',   body: data }),
  getReservation:    (id)       => request(`/api/reservations/${id}`),
  updateReservation: (id, data) => request(`/api/reservations/${id}`, { method: 'PUT',    body: data }),
  cancelReservation: (id)       => request(`/api/reservations/${id}`, { method: 'DELETE' }),
  getUnseenCount:    ()         => request('/api/reservations/unseen-count'),
  markSeen:          (id)       => request(`/api/reservations/${id}/seen`, { method: 'PUT' }),
  markNoShow:        (id)       => request(`/api/reservations/${id}`, { method: 'PUT', body: { status: 'no_show' } }),

  // Availability
  getAvailability: (date, time, party_size) =>
    request(`/api/availability?date=${date}&time=${time}&party_size=${party_size}`),

  // Tables
  getTables:           ()        => request('/api/tables'),
  getLocations:        ()        => request('/api/tables/locations'),
  getUnplacedCount:    ()        => request('/api/tables/unplaced-count'),
  updateTable: (id, data)        => request(`/api/tables/${id}`,  { method: 'PUT',  body: data }),
  createTable: (data)            => request('/api/tables',        { method: 'POST', body: data }),
  deleteTable: (id)              => request(`/api/tables/${id}`,  { method: 'DELETE' }),

  // Floor plan
  saveFloorPlan: (tables)    => request('/api/floorplan',     { method: 'PUT',  body: { tables } }),

  // Menu
  getMenu:         ()         => request('/api/menu'),
  scanMenu:        (image)    => request('/api/menu/scan',   { method: 'POST',   body: { image } }),
  bulkCreateMenu:  (items)    => request('/api/menu/bulk',   { method: 'POST',   body: { items } }),
  createMenuItem:  (data)     => request('/api/menu',        { method: 'POST',   body: data }),
  updateMenuItem:  (id, data) => request(`/api/menu/${id}`,  { method: 'PUT',    body: data }),
  deleteMenuItem:  (id)       => request(`/api/menu/${id}`,  { method: 'DELETE' }),
  clearMenu:       ()         => request('/api/menu',        { method: 'DELETE' }),

  // Settings
  getSettings:    ()     => request('/api/settings'),
  updateSettings: (data) => request('/api/settings', { method: 'PUT', body: data }),

  // Analytics
  getAnalytics: (days = 30) => request(`/api/analytics?days=${days}`),

  // Capacity
  getCapacity: (days = 60) => request(`/api/capacity?days=${days}`),

  // Waitlist
  getWaitlist:      ()        => request('/api/waitlist'),
  updateWaitlist:   (id, data)=> request(`/api/waitlist/${id}`, { method: 'PUT',    body: data }),
  deleteWaitlist:   (id)      => request(`/api/waitlist/${id}`, { method: 'DELETE' }),
};
